import { useEffect, useState, useRef } from 'react';
import { api, formatDate } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Paperclip, Upload, Trash, FilePdf, FileImage, FileText, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const iconFor = (ct = '') => {
  if (ct.startsWith('image/')) return FileImage;
  if (ct.includes('pdf')) return FilePdf;
  return FileText;
};

export default function Attachments({ entityType, entityId, title = 'المرفقات' }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const load = () => {
    if (!entityId) return;
    api.get('/attachments', { params: { entity_type: entityType, entity_id: entityId } })
      .then(({ data }) => setItems(data))
      .catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [entityType, entityId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('حجم الملف يتجاوز 25 ميجا'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/attachments/upload?entity_type=${entityType}&entity_id=${entityId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('تم رفع الملف');
      load();
    } catch (err) {
      toast.error('فشل الرفع: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const del = async (id) => {
    if (!window.confirm('حذف المرفق؟')) return;
    try {
      await api.delete(`/attachments/${id}`);
      toast.success('تم الحذف');
      load();
    } catch { toast.error('فشل الحذف'); }
  };

  const download = (att) => {
    const token = localStorage.getItem('token');
    const url = `${api.defaults.baseURL}/attachments/${att.id}/download?auth=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
  };

  const formatSize = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div data-testid="attachments-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Paperclip size={16} className="text-[#B4935A]" /> {title}
          <span className="text-xs text-slate-500 font-normal">({items.length})</span>
        </div>
        <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="upload-file-button">
          <Upload className="ms-1" size={14} /> {uploading ? 'جاري الرفع...' : 'رفع ملف'}
        </Button>
        <input ref={inputRef} type="file" onChange={upload} className="hidden" data-testid="attachment-file-input" />
      </div>

      {items.length === 0 ? (
        <div className="text-xs text-slate-500 text-center py-6 border border-dashed border-slate-200 rounded-md">
          لا توجد مرفقات
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((att) => {
            const Icon = iconFor(att.content_type);
            return (
              <div key={att.id} className="flex items-center gap-3 p-2.5 border border-slate-200 rounded-md hover:border-[#B4935A] transition-colors">
                <div className="w-9 h-9 rounded-md bg-slate-50 flex items-center justify-center shrink-0">
                  <Icon size={18} className="text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{att.original_filename}</div>
                  <div className="text-xs text-slate-500 mono">{formatSize(att.size)} · {att.uploaded_by_name} · {formatDate(att.created_at)}</div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => download(att)} data-testid={`download-${att.id}`}>
                  <DownloadSimple size={16} />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => del(att.id)}>
                  <Trash size={16} className="text-red-600" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
